import { BieOnboardingApiError } from './bieonboarding.api.error';

export const requirePositiveId = (
  value: unknown,
  fieldName: string
): string => {
  if (value === null || value === undefined) {
    throw new BieOnboardingApiError(`${fieldName} is required.`);
  }

  const normalized = String(value).trim();

  if (!normalized) {
    throw new BieOnboardingApiError(`${fieldName} is required.`);
  }

  if (!/^\d+$/.test(normalized)) {
    throw new BieOnboardingApiError(`${fieldName} must be a positive integer.`);
  }

  if (BigInt(normalized) <= 0) {
    throw new BieOnboardingApiError(`${fieldName} must be greater than zero.`);
  }

  return normalized;
};

export const optionalPositiveId = (
  value: unknown,
  fieldName: string
): string | null => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }

  return requirePositiveId(value, fieldName);
};

export const requireText = (value: unknown, fieldName: string): string => {
  if (value === null || value === undefined) {
    throw new BieOnboardingApiError(`${fieldName} is required.`);
  }

  const normalized = String(value).trim();

  if (!normalized) {
    throw new BieOnboardingApiError(`${fieldName} is required.`);
  }

  return normalized;
};

export const requiredUpperText = (value: unknown, fieldName: string): string | null => {
  const normalized = requireText(value, fieldName);

  return normalized ? normalized.toUpperCase() : null;
};

export const optionalText = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim();

  return normalized || null;
};

export const optionalUpperText = (value: unknown): string | null => {
  const normalized = optionalText(value);

  return normalized ? normalized.toUpperCase() : null;
};

export const optionalFlag = (
  value: unknown,
  fieldName: string
): 0 | 1 | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value === true || value === 'true' || value === '1') {
    return 1;
  }

  if (value === false || value === 'false' || value === '0') {
    return 0;
  }

  const normalized = Number(value);

  if (normalized !== 0 && normalized !== 1) {
    throw new BieOnboardingApiError(`${fieldName} must be 0 or 1.`);
  }

  return normalized;
};

export const optionalStatus = (value: unknown): 0 | 1 | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalized = Number(value);

  if (normalized !== 0 && normalized !== 1) {
    throw new BieOnboardingApiError('Status must be 0 or 1.');
  }

  return normalized;
};

export const normalizePageOffset = (value: unknown): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const normalized = Number(value);

  if (!Number.isInteger(normalized) || normalized < 0) {
    throw new BieOnboardingApiError(
      'Page offset must be a non-negative integer.'
    );
  }

  return normalized;
};

export const normalizePageLimit = (value: unknown): number => {
  if (value === null || value === undefined || value === '') {
    return 100;
  }

  const normalized = Number(value);

  if (!Number.isInteger(normalized) || normalized < 1) {
    throw new BieOnboardingApiError('Page limit must be a positive integer.');
  }

  return Math.min(normalized, 1000);
};

// Generics
export const normalizeRequiredCode = (classCode: any, fieldName: string) => requiredUpperText(
  classCode, fieldName
);

export const normalizeCode = (classCode: any) => optionalUpperText(
  classCode,
);

// Specifics

export const normalizeLoadSessionId = (loadSessionId: any) =>  requirePositiveId(
    loadSessionId,
    'Load Session ID',
  );

export const normalizeLoadSessionStamp = (stamp: any) => requireText(
  stamp,
  'Load Session stamp',
);

export const normalizeImportId = (importId: any) => optionalPositiveId(
  importId,
  'Import ID',
);

export const normalizeImportItemId = (importItemId: any) => optionalPositiveId(
  importItemId,
  'Import Item ID',
);

export const normalizeSeverityCode = (severityCode: any) => optionalUpperText(
  severityCode,
);

export const normalizeRuleCode = (ruleCode: any) => optionalUpperText(
  ruleCode,
);

export const normalizeClassCode = (classCode: any) => optionalUpperText(
  classCode,
);

export const normalizeBlocking = (blocking: any) => optionalFlag(
  blocking,
  'Blocking filter',
);

export const normalizeSearch = (search: any) => optionalText(
  search,
);

export const normalizeStatus = (status: any) => optionalStatus(
  status,
);

